/**
 * Centralized logging utility for consistent error and debug logging
 * In production, can be extended to send logs to monitoring services
 */

const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'

class Logger {
  constructor(context = 'App') {
    this.context = context
  }

  /**
   * Log error with context
   * @param {string} message - Error message
   * @param {Error|Object} error - Error object or additional data
   * @param {Object} metadata - Additional metadata
   */
  error(message, error = null, metadata = {}) {
    const logData = {
      context: this.context,
      message,
      timestamp: new Date().toISOString(),
      ...metadata
    }

    if (error) {
      logData.error = {
        message: error.message || error,
        stack: error.stack,
        response: error.response?.data
      }
    }

    // In development, use console.error with full details
    if (isDevelopment) {
      console.error(`[${this.context}] ${message}`, error || '', metadata)
    } else {
      // In production, send to monitoring service (e.g., Sentry, DataDog)
      // For now, just log structured data
      console.error(JSON.stringify(logData))
    }

    // Could add integration with error tracking service here
    // Example: Sentry.captureException(error, { contexts: { custom: logData } })
  }

  /**
   * Log warning with context
   * @param {string} message - Warning message
   * @param {Object} data - Additional data
   */
  warn(message, data = {}) {
    if (isDevelopment) {
      console.warn(`[${this.context}] ${message}`, data)
    }
  }

  /**
   * Log info (development only)
   * @param {string} message - Info message
   * @param {Object} data - Additional data
   */
  info(message, data = {}) {
    if (isDevelopment) {
      console.info(`[${this.context}] ${message}`, data)
    }
  }

  /**
   * Log debug (development only)
   * @param {string} message - Debug message
   * @param {Object} data - Additional data
   */
  debug(message, data = {}) {
    if (isDevelopment) {
      console.debug(`[${this.context}] ${message}`, data)
    }
  }
}

/**
 * Create a logger instance for a specific context
 * @param {string} context - Context name (e.g., 'Channels', 'Auth', 'API')
 * @returns {Logger}
 */
export function createLogger(context) {
  return new Logger(context)
}

/**
 * Default logger instance
 */
export const logger = new Logger()

export default logger
